import LeaderboardItem from "@component/leaderboard-item";
import { useUserProfileContext } from "@context/user_profile";
import { InterviewLeaderboardItem } from "@type/interview";
import { UsersIcon } from "lucide-react";

interface Props {
    isLoadingScore?: boolean
    isLoading?: boolean
    isPendingProcessing?: boolean
    interviewName: string
    items: InterviewLeaderboardItem[];
}

export default function PracticeInterviewLeaderboard(props: Props) {
    const [, currentUserInfo] = useUserProfileContext();
    return (
        <div className="space-y-4 pr-0 sm:pr-6">
            <div className="text-left">
                <h2 className="text-xl font-bold text-gray-900">
                    {props.interviewName}
                </h2>
                <p className="text-sm text-gray-600">
                    See where you rank on this interview.
                </p>
            </div>
            {props.isLoading ? (
                <div className="pl-8 h-[calc(100vh-200px)] flex items-center justify-center">
                    <div className="text-center">
                        <div className="flex space-x-2 justify-center mb-4">
                            {/* Three bouncing blue dots */}
                            <div
                                className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                                style={{ animationDelay: "0ms" }}
                            ></div>
                            <div
                                className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                                style={{ animationDelay: "150ms" }}
                            ></div>
                            <div
                                className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                                style={{ animationDelay: "300ms" }}
                            ></div>
                        </div>
                        <p className="text-gray-600 text-sm">Loading leaderboard...</p>
                    </div>
                </div>
            ) : (
                <div className="border border-gray-200 rounded-lg flex flex-col">
                    <div
                        className="flex justify-between items-center p-4 border-b border-gray-200 sticky top-0 bg-gray-50 z-10"
                        style={{ backgroundColor: "#f8fafc" }}
                    >
                        <h3 className="font-medium">Leaderboard</h3>
                        <div className="text-sm">
                            <span
                                className="text-blue-600 font-medium"
                                style={{
                                    backgroundColor: "#ffffff",
                                    color: "#000000",
                                    paddingTop: "6px",
                                    paddingRight: "15px",
                                    paddingBottom: "6px",
                                    paddingLeft: "15px",
                                    border: "1px solid rgb(228, 228, 231)",
                                    borderRadius: "1rem",
                                }}
                            >
                                All time
                            </span>
                        </div>
                    </div>
                    <div className="sm:h-[calc(100vh-200px)] overflow-y-scroll">
                        {props.items.length === 0 ? (
                            <div className="flex-1 flex flex-col items-center justify-center py-12 px-6">
                                <div className="text-center">
                                    <div className="bg-gray-100 p-4 rounded-full mb-4 inline-block">
                                        <UsersIcon className="h-8 w-8 text-gray-400" />
                                    </div>

                                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                                        No leaderboard data available.
                                    </h3>
                                    <p className="text-gray-500 max-w-sm">
                                        Leaderboard rankings are currently not available, please check
                                        again later.
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div
                                className="divide-y divide-gray-100 flex-1"
                            >
                                {props.isLoadingScore && !props.isPendingProcessing && (
                                    <div
                                        className={`flex items-center justify-between py-3 px-4 bg-blue-50`}
                                    >
                                        <div className="flex items-center">
                                            <div
                                                className="w-8 text-gray-500 text-sm"
                                                style={{ paddingRight: "8px" }}
                                            >
                                                &nbsp;
                                            </div>
                                            <div className="w-8 h-8 rounded-full overflow-hidden mr-4">
                                                <img
                                                    onError={(e) => {
                                                        e.currentTarget.src = `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/default_avatar.png`;
                                                    }}
                                                    src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${currentUserInfo?.profile_picture_path}`}
                                                />
                                            </div>
                                            <div className="font-medium">You</div>
                                        </div>
                                        <div className="text-gray-700">
                                            <div className="flex items-center space-x-1">
                                                <div
                                                    className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"
                                                    style={{ animationDelay: "0ms" }}
                                                />

                                                <div
                                                    className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"
                                                    style={{ animationDelay: "150ms" }}
                                                />

                                                <div
                                                    className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"
                                                    style={{ animationDelay: "300ms" }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {props.items.map((entry, index) => {
                                    const isCurrentUser = currentUserInfo && entry.user_id === currentUserInfo.id;
                                    if (props.isLoadingScore && isCurrentUser) {
                                        return null; // Skip rendering this entry as it's already shown above
                                    }
                                    if (props.isPendingProcessing && isCurrentUser) {
                                        return null; // Hide "You" entirely while score is still being processed
                                    }
                                    return (
                                        <LeaderboardItem
                                            key={index}
                                            rank={entry.rank}
                                            name={isCurrentUser ? "You" : entry.initials}
                                            points={entry.score}
                                            avatar={entry.photo_profile}
                                        />
                                    )
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
